// lib/supabase/browser.ts
"use client";

import type { Database } from "@/types/supabase";
import { type SupabaseClient, createClient } from "@supabase/supabase-js";

type BrowserClient = SupabaseClient<Database>;
type GetAccessToken = () => Promise<string | null>;

let browserClient: BrowserClient | null = null;

// Mutable getter so the singleton can start “anon” and later become authed.
let tokenGetter: GetAccessToken | null = null;

// Lazy env resolution so Biome is happy (no non-null assertions).
let envCache: { url: string; anonKey: string } | null = null;

function getSupabaseEnv() {
  if (envCache) return envCache;

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!url) throw new Error("[supabase] Missing NEXT_PUBLIC_SUPABASE_URL");
  if (!anonKey) throw new Error("[supabase] Missing NEXT_PUBLIC_SUPABASE_ANON_KEY");

  envCache = { url, anonKey };
  return envCache;
}

function makeClient(getterRef: { current: GetAccessToken | null }): BrowserClient {
  const { url, anonKey } = getSupabaseEnv();

  return createClient<Database>(url, anonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      fetch: async (input, init = {}) => {
        const token = getterRef.current ? await getterRef.current() : null;

        const headers = new Headers(init.headers);
        if (token) headers.set("Authorization", `Bearer ${token}`);

        return fetch(input, { ...init, headers });
      },
    },
  });
}

const getterRef = { current: tokenGetter as GetAccessToken | null };

export function clientSupabase(getAccessToken?: GetAccessToken): BrowserClient {
  if (getAccessToken) {
    tokenGetter = getAccessToken;
    getterRef.current = getAccessToken;
  }

  if (browserClient) return browserClient;

  browserClient = makeClient(getterRef);
  return browserClient;
}

export function newBrowserSupabaseClient(getAccessToken?: GetAccessToken): BrowserClient {
  const localRef = { current: getAccessToken ?? tokenGetter };
  return makeClient(localRef);
}
